import { CaretDown, Check, UsersThree } from '@phosphor-icons/react';
import {
  addDays,
  NOTE_MAX,
  REMINDER_DEFAULTS,
  suggestIcon,
  weekdayOf,
  type Assignees,
  type Note,
  type Remind,
  type Reward,
} from '@shh/shared';
import {
  Avatar,
  Button,
  ChoiceGroup,
  Icon,
  MultiChoice,
  Text,
  TextField,
  type Choice,
} from '@shh/ui';
import { useState, type ReactNode } from 'react';
import { saveNote, saveReward } from '../data/api';
import { dayLabel } from '../data/agenda';
import { kids, type M } from '../data/derive';
import { useFamily, type WithId } from '../data/family';
import { iconFor } from '../lib/icons';
import { localeTag } from '../lib/locale';
import { formsText } from '../i18n/forms';

/*
 * Creating things in one sheet: the main question big ("Was?"), everything else with
 * sensible defaults and quick chips, extras folded away. The picture is chosen from the title.
 */

export const personChoice = (m: M): Choice => ({
  value: m.id,
  label: m.name,
  picture: <Avatar name={m.name} color={m.color} emoji={m.emoji} size="sm" decorative />,
});
const ALL: Choice = {
  value: 'all',
  label: formsText.everyone,
  picture: (
    <span className="grid size-avatar-sm place-items-center rounded-avatar bg-accent-secondary text-accent-on-accent">
      <Icon icon={UsersThree} size="sm" weight="fill" />
    </span>
  ),
};

export function TitleField({
  value,
  onChange,
  label,
  placeholder,
  fallback = 'check',
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  placeholder: string;
  fallback?: Parameters<typeof suggestIcon>[1];
}) {
  return (
    <div className="flex items-end gap-gap-md">
      <span
        aria-hidden
        className="grid size-touch-target shrink-0 place-items-center rounded-control bg-accent-secondary-soft text-accent-secondary"
      >
        <Icon icon={iconFor(suggestIcon(value, fallback))} size="lg" />
      </span>
      <TextField
        label={label}
        size="lg"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoFocus
        className="flex-1"
      />
    </div>
  );
}

export function More({
  children,
  label = formsText.more,
}: {
  children: ReactNode;
  label?: string;
}) {
  return (
    <details className="group flex flex-col gap-gap-md">
      <summary className="flex min-h-touch-min cursor-pointer list-none items-center gap-gap-sm rounded-control font-semibold text-text-secondary outline-none focus-visible:outline-3 focus-visible:outline-border-focus">
        <Icon
          icon={CaretDown}
          size="sm"
          weight="bold"
          className="transition group-open:rotate-180"
        />
        {label}
      </summary>
      <div className="flex flex-col gap-gap-md pt-gap-sm">{children}</div>
    </details>
  );
}

export function DateChips({
  value,
  onChange,
  label = formsText.when,
}: {
  value: string;
  onChange: (d: string) => void;
  label?: string;
}) {
  const f = useFamily();
  const quick = Array.from({ length: 7 }, (_, i) => addDays(f.today, i));
  // "Anderer Tag" must open the date field even while the value is still one of the quick days
  const [other, setOther] = useState(!quick.includes(value));
  const inQuick = !other && quick.includes(value);
  return (
    <div className="flex flex-col gap-gap-sm">
      <ChoiceGroup
        label={label}
        value={inQuick ? value : 'other'}
        onChange={(v) => {
          setOther(v === 'other');
          if (v !== 'other') onChange(v);
        }}
        choices={[
          ...quick.map((d) => {
            const l = dayLabel(f, d);
            return { value: d, label: l.label, description: l.date };
          }),
          { value: 'other', label: formsText.otherDay },
        ]}
      />
      {!inQuick && (
        <TextField
          label={formsText.date}
          type="date"
          value={value}
          onChange={(v) => v && onChange(v)}
        />
      )}
    </div>
  );
}

export function Actions({
  onCancel,
  onSave,
  saveLabel,
  disabled,
  busy,
  error,
}: {
  onCancel: () => void;
  onSave: () => void;
  saveLabel: string;
  disabled?: boolean;
  busy?: boolean;
  error?: string;
}) {
  // Save and cancel always in sight (Mike, 20.10.: "die anderen Apps haben speichern … immer im
  // Sichtfeld") – stuck to the bottom of the sheet, so a quick entry never scrolls to the end
  return (
    <div className="sticky -bottom-inset-lg z-10 -mx-inset-lg -mb-inset-lg flex flex-col gap-gap-sm border-t border-border-subtle bg-surface-overlay px-inset-lg pt-inset-sm pb-inset-lg">
      {error && (
        <p role="alert" className="font-semibold text-feedback-danger">
          {error}
        </p>
      )}
      <div className="grid grid-cols-[auto_1fr] gap-gap-sm">
        <Button variant="secondary" onPress={onCancel}>
          {formsText.cancel}
        </Button>
        <Button size="lg" icon={Check} onPress={onSave} isDisabled={disabled || busy}>
          {busy ? formsText.saving : saveLabel}
        </Button>
      </div>
    </div>
  );
}

export function useSave(onDone: () => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const save = (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(undefined);
    fn()
      .then(onDone)
      .catch(() => {
        setError(formsText.failed);
        setBusy(false);
      });
  };
  return { busy, error, save };
}

/** People picker: "Alle" plus avatars. Returns 'all' or the chosen ids. */
export function WhoField({
  label,
  value,
  onChange,
  people,
  allowAll = true,
  layout = 'tile',
}: {
  label: string;
  value: Assignees;
  onChange: (a: Assignees) => void;
  people: M[];
  allowAll?: boolean;
  layout?: 'tile' | 'person';
}) {
  const selected = value === 'all' ? ['all'] : value;
  return (
    <MultiChoice
      label={label}
      layout={layout}
      value={selected}
      onChange={(next) => {
        const added = next.find((v) => !selected.includes(v));
        if (added === 'all') return onChange('all');
        const ids = next.filter((v) => v !== 'all');
        onChange(ids.length ? ids : allowAll ? 'all' : selected.filter((v) => v !== 'all'));
      }}
      choices={[...(allowAll ? [ALL] : []), ...people.map(personChoice)]}
    />
  );
}

// ---------------------------------------------------------------------------------------------
/** "Erinnern" (1.13): the family default unless chosen otherwise. */
export type RemindChoice = 'default' | '10' | '30' | '60' | 'eve' | 'none';
export const toRemindChoice = (r?: Remind): RemindChoice =>
  r === undefined ? 'default' : r === 'eve' || r === 'none' ? r : (String(r) as RemindChoice);
export const fromRemindChoice = (c: RemindChoice): Remind | undefined =>
  c === 'default' ? undefined : c === 'eve' || c === 'none' ? c : Number(c);

export function RemindField({
  value,
  onChange,
  defaultLabel,
}: {
  value: RemindChoice;
  onChange: (v: RemindChoice) => void;
  /** "Standard (30 Min.)" */
  defaultLabel: string;
}) {
  return (
    <ChoiceGroup<RemindChoice>
      label={formsText.remind}
      description={formsText.remindHint}
      value={value}
      onChange={onChange}
      choices={[
        { value: 'default', label: defaultLabel },
        { value: '10', label: formsText.min10 },
        { value: '60', label: formsText.hour1 },
        { value: 'eve', label: formsText.eve },
        { value: 'none', label: formsText.none },
      ]}
    />
  );
}

/** "Standard (30 Min.)" / "Standard (Vorabend)" – what the family setting means for this event. */
export function defaultRemindLabel(f: ReturnType<typeof useFamily>, allDay: boolean) {
  const r = { ...REMINDER_DEFAULTS, ...(f.household?.reminders ?? {}) };
  if (allDay)
    return r.eveHour === null ? formsText.defaultNone : formsText.defaultEve(String(r.eveHour));
  return r.eventMinutes
    ? r.eventMinutes < 60
      ? formsText.defaultMin(String(r.eventMinutes))
      : formsText.defaultHours(String(r.eventMinutes / 60))
    : formsText.defaultNone;
}

// ---------------------------------------------------------------------------------------------
/**
 * Star amount: the usual steps as quick choices, plus "Andere …" for any number in range.
 * An existing value outside the steps opens directly in the number field.
 */
export function StarAmount({
  label,
  presets,
  format,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  presets: string[];
  format: (v: string) => string;
  value: string;
  onChange: (v: string) => void;
  min: number;
  max: number;
}) {
  const [custom, setCustom] = useState(!presets.includes(value));
  return (
    <div className="flex flex-col gap-gap-sm">
      <ChoiceGroup
        label={label}
        value={custom ? 'custom' : value}
        onChange={(v) => {
          setCustom(v === 'custom');
          if (v !== 'custom') onChange(v);
        }}
        choices={[
          ...presets.map((p) => ({ value: p, label: format(p) })),
          { value: 'custom', label: formsText.other },
        ]}
      />
      {custom && (
        <TextField
          label={formsText.starCount}
          description={formsText.range(String(min), max.toLocaleString(localeTag))}
          type="number"
          inputMode="numeric"
          value={value}
          onChange={(v) => onChange(v.replace(/[^0-9]/g, ''))}
        />
      )}
    </div>
  );
}

export const inRange = (v: string, min: number, max: number) =>
  /^[0-9]+$/.test(v) && Number(v) >= min && Number(v) <= max;
export function RewardForm({ onDone, reward }: { onDone: () => void; reward?: Reward }) {
  const f = useFamily();
  const [title, setTitle] = useState(reward?.title ?? '');
  const [cost, setCost] = useState(String(reward?.cost ?? 100));
  const [who, setWho] = useState<Assignees>(reward?.memberIds.length ? reward.memberIds : 'all');
  const { busy, error, save } = useSave(onDone);
  return (
    <form className="flex flex-col gap-gap-lg" onSubmit={(e) => e.preventDefault()}>
      <TitleField
        label={formsText.reward}
        placeholder={formsText.rewardExample}
        value={title}
        onChange={setTitle}
        fallback="gift"
      />
      <StarAmount
        label={formsText.costs}
        presets={['30', '50', '100', '200', '500']}
        format={(c) => c + ' ⭐'}
        value={cost}
        onChange={setCost}
        min={1}
        max={100000}
      />
      <WhoField label={formsText.forWhom} value={who} onChange={setWho} people={kids(f)} />
      <Text size="sm" tone="muted">
        {formsText.rewardTip}
      </Text>
      <Actions
        onCancel={onDone}
        saveLabel={reward ? formsText.save : formsText.addReward}
        disabled={!title.trim() || !inRange(cost, 1, 100000)}
        busy={busy}
        error={error}
        onSave={() =>
          save(() =>
            saveReward(f.householdId, {
              id: reward?.id,
              title: title.trim(),
              icon:
                reward && title.trim() === reward.title ? reward.icon : suggestIcon(title, 'gift'),
              cost: Number(cost),
              memberIds: who === 'all' ? [] : who,
            }),
          )
        }
      />
    </form>
  );
}

// ---------------------------------------------------------------------------------------------
/** A note for the pinboard – one sentence, optionally only until a day. */
export function NoteForm({ onDone, note }: { onDone: () => void; note?: WithId<Note> }) {
  const f = useFamily();
  const [text, setText] = useState(note?.text ?? '');
  const sunday = addDays(f.today, 7 - weekdayOf(f.today));
  const [until, setUntil] = useState<string>(note?.until ?? 'none');
  const { busy, error, save } = useSave(onDone);
  const quick = ['none', f.today, addDays(f.today, 1), sunday];
  return (
    <form className="flex flex-col gap-gap-lg" onSubmit={(e) => e.preventDefault()}>
      <TextField
        label={formsText.noteAsk}
        size="lg"
        value={text}
        onChange={setText}
        placeholder={formsText.noteExample}
        maxLength={NOTE_MAX}
        autoFocus
      />
      <ChoiceGroup
        label={formsText.howLong}
        value={until}
        onChange={setUntil}
        choices={[
          { value: 'none', label: formsText.untilRemoved },
          { value: f.today, label: formsText.today },
          { value: addDays(f.today, 1), label: formsText.untilTomorrow },
          ...(sunday > addDays(f.today, 1)
            ? [{ value: sunday, label: formsText.untilSunday }]
            : []),
          // an older "bis" day stays selectable when fixing the text
          ...(!quick.includes(until)
            ? [{ value: until, label: formsText.untilDay(dayLabel(f, until, true).label) }]
            : []),
        ]}
      />
      <Actions
        onCancel={onDone}
        saveLabel={note ? formsText.save : formsText.pin}
        disabled={!text.trim()}
        busy={busy}
        error={error}
        onSave={() =>
          save(() =>
            saveNote(f.householdId, {
              id: note?.id,
              text: text.trim(),
              until: until === 'none' ? undefined : until,
              createdBy: note ? undefined : f.meId,
            }),
          )
        }
      />
    </form>
  );
}
